import React, { forwardRef } from 'react';
import { Rows, SquaresFour } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';

/**
 * ViewToggle, grid vs rows.
 *
 * EXACTLY TWO VIEWS, and the second one is ROWS. It was rows, briefly became a
 * horizontal card rail (`Columns` glyph, "Rail view"), and is rows again on the
 * designer's call after seeing the rail on the marketplace. The glyph is `Rows`
 * — stacked full-width bars — because the toggle has to say what the layout
 * will be before you press it, and `Columns` (upright bars) described the rail.
 *
 * The `list` VALUE is unchanged through all of it. It is the second view
 * whatever that view looks like, it is persisted in nothing, and renaming it
 * would touch every call site to say the same thing.
 *
 * CSS: `desktop/system.css` `.dsk-viewtoggle`.
 * Behaviour: none of the numbered reference behaviours.
 *
 * The 38x36 icon-only special case: `.dsk-viewtoggle` is the ONE segmented
 * control whose segments are a fixed 38x36 grid cell rather than the generic
 * `.dsk-segmented`'s 36px-tall text button. The two halves are butted together
 * behind one hairline so it reads as a single control with two states.
 *
 * The chosen half fills with INK, not accent: swapping the whole view is a
 * committed command, the same rule as `.dsk-chip.is-active`. The icon in the
 * chosen half goes to fill weight because fill is the active weight, and it
 * inherits `--ds-ink-contrast` from the ink surface rather than tinting accent.
 *
 * Icon-only means each half must carry its own accessible name; the wrapper is
 * a labelled group so a screen reader announces what the pair controls.
 */
export const ViewToggle = forwardRef(function ViewToggle(
  {
    value = 'grid',
    onChange,
    label = 'View',
    gridLabel = 'Grid view',
    listLabel = 'Rows view',
    className,
    ...rest
  },
  ref,
) {
  const select = (next) => {
    if (next !== value && onChange) onChange(next);
  };

  return (
    <div ref={ref} className={cn('dsk-viewtoggle', className)} role="group" aria-label={label} {...rest}>
      <button
        type="button"
        aria-pressed={value === 'grid'}
        aria-label={gridLabel}
        onClick={() => select('grid')}
      >
        <Icon as={SquaresFour} active={value === 'grid'} />
      </button>
      <button
        type="button"
        aria-pressed={value === 'list'}
        aria-label={listLabel}
        onClick={() => select('list')}
      >
        <Icon as={Rows} active={value === 'list'} />
      </button>
    </div>
  );
});

export default ViewToggle;
